import type { ApiResponse, AuthUser, PaginationMeta } from '@sfa/shared';
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

/**
 * HTTP client.
 *
 * The access token is held in a module variable, never in localStorage. An XSS
 * payload can read localStorage trivially; it cannot read a closure variable
 * without already executing in this exact context, and the token dies with the
 * tab either way. The long-lived refresh token is an HttpOnly cookie the
 * browser sends automatically and JavaScript cannot touch at all.
 */

let accessToken: string | null = null;
let onUnauthenticated: (() => void) | null = null;

/**
 * Bumped on logout, checked by a resolving restoreSession() below.
 *
 * Without it, a restore that was already in flight when the user logged out
 * can resolve afterwards with a perfectly valid token and silently
 * re-authenticate them.
 */
let authEpoch = 0;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Logout's counterpart to setAccessToken(null): also voids any in-flight restore. */
export function clearSession(): void {
  authEpoch += 1;
  accessToken = null;
}

export function setUnauthenticatedHandler(handler: () => void): void {
  onUnauthenticated = handler;
}

export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api/v1',
  // Required for the refresh cookie to be sent and stored.
  withCredentials: true,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

function isAuthUrl(url: string | undefined): boolean {
  return Boolean(url?.includes('/auth/login') || url?.includes('/auth/refresh'));
}

/**
 * Exchange the httpOnly refresh cookie for a fresh access token.
 *
 * The one and only path that talks to POST /auth/refresh, shared by app-boot
 * restoration, a 401 retry, and the request interceptor below. A rotating
 * refresh token is single-use — two independent callers racing to redeem it
 * would have one succeed and the other invalidate the session — so every
 * caller in the same recovery window shares this one in-flight promise via
 * `pendingRestore`, exactly as the old `refreshPromise` shared 401 retries.
 *
 * Uses the raw `axios` client, not `api`: going through `api` would re-enter
 * the request interceptor below, which is exactly the recursion the
 * isAuthUrl checks exist to prevent.
 */
let pendingRestore: Promise<{ accessToken: string; user: AuthUser } | null> | null = null;

export function restoreSession(): Promise<{ accessToken: string; user: AuthUser } | null> {
  const epoch = authEpoch;

  pendingRestore ??= (async () => {
    try {
      const response = await axios.post<ApiResponse<{ accessToken: string; user: AuthUser }>>(
        `${api.defaults.baseURL}/auth/refresh`,
        {},
        { withCredentials: true },
      );

      if (!response.data.success) return null;

      // A logout landed while this was in flight. Its cookie exchange still
      // succeeded, but applying the result now would silently sign the user
      // back in right after they signed out.
      if (epoch !== authEpoch) return null;

      accessToken = response.data.data.accessToken;

      return response.data.data;
    } catch {
      return null;
    } finally {
      // Cleared on the next tick so callers awaiting this promise all resolve
      // from the same in-flight request.
      setTimeout(() => {
        pendingRestore = null;
      }, 0);
    }
  })();

  return pendingRestore;
}

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  /*
   * The session-restore race: a request can be built at the exact moment the
   * in-memory token has been cleared — app boot, or the tab having resumed
   * mid-camera-capture with the token wiped — before restore() has finished
   * exchanging the refresh cookie. Firing immediately here would send it
   * unauthenticated even though the session is perfectly recoverable a
   * moment later.
   *
   * Every protected route in this app renders behind RequireAuth, which
   * withholds children until the boot restore has settled one way or the
   * other — so a live page never fires this with a truly dead session, only
   * with one that is mid-recovery. restoreSession()'s own single-flight
   * guard is what keeps this from becoming a refresh storm: whichever
   * request gets here first starts the one exchange, every other
   * request — however many arrive at once — joins that same promise instead
   * of starting its own.
   */
  if (!accessToken && !isAuthUrl(config.url)) {
    await restoreSession();
  }

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  /*
   * A multipart upload must not inherit the instance's JSON content type.
   *
   * `headers: { 'Content-Type': 'application/json' }` above is an instance
   * default, and axios applies it to FormData too rather than standing aside.
   * The boundary is then missing, so the server's JSON body parser reads the
   * image bytes and fails — every product image upload died with a parse
   * error that named neither the file nor the field.
   *
   * Deleting it here lets axios generate `multipart/form-data; boundary=…`
   * itself. Done once in the interceptor so any future upload is covered,
   * rather than at each call site where it will eventually be forgotten.
   */
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }

  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error?: { code: string; message: string } }>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };

    if (error.response?.status === 401 && original && !original._retried && !isAuthUrl(original.url)) {
      original._retried = true;

      const result = await restoreSession();

      if (result) {
        original.headers.Authorization = `Bearer ${result.accessToken}`;

        return api(original);
      }

      accessToken = null;
      onUnauthenticated?.();
    }

    return Promise.reject(error);
  },
);

// ---------------------------------------------------------------------
// Typed helpers
// ---------------------------------------------------------------------

export interface ListResult<T> {
  data: T[];
  meta: PaginationMeta;
}

/** Unwraps the { success, data } envelope so callers work with plain data. */
export async function apiGet<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await api.get<ApiResponse<T>>(url, { params });

  if (!response.data.success) throw new ApiError(response.data.error);

  return response.data.data;
}

export async function apiList<T>(url: string, params?: Record<string, unknown>): Promise<ListResult<T>> {
  const response = await api.get<{ success: true; data: T[]; meta: PaginationMeta }>(url, { params });

  return { data: response.data.data, meta: response.data.meta };
}

export async function apiPost<T>(url: string, body?: unknown): Promise<T> {
  const response = await api.post<ApiResponse<T>>(url, body);

  if (!response.data.success) throw new ApiError(response.data.error);

  return response.data.data;
}

export async function apiPatch<T>(url: string, body?: unknown): Promise<T> {
  const response = await api.patch<ApiResponse<T>>(url, body);

  if (!response.data.success) throw new ApiError(response.data.error);

  return response.data.data;
}

export async function apiDelete(url: string, body?: unknown): Promise<void> {
  await api.delete(url, { data: body });
}

/**
 * A typed error that preserves the API's field-level details, so a form can
 * highlight the offending input instead of showing a detached banner.
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly details?: Record<string, string[]>;

  constructor(error: { code: string; message: string; details?: Record<string, string[]> }) {
    super(error.message);
    this.name = 'ApiError';
    this.code = error.code;
    this.details = error.details;
  }
}

/** Normalises anything thrown by axios into a message plus field errors. */
export function parseApiError(error: unknown): { message: string; details?: Record<string, string[]> } {
  if (error instanceof ApiError) {
    return { message: error.message, details: error.details };
  }

  if (axios.isAxiosError(error)) {
    const payload = error.response?.data as
      | { error?: { message?: string; details?: Record<string, string[]> } }
      | undefined;

    if (payload?.error) {
      return { message: payload.error.message ?? 'Request failed.', details: payload.error.details };
    }

    if (error.code === 'ECONNABORTED') {
      return { message: 'The request timed out. Please try again.' };
    }

    if (!error.response) {
      return { message: 'Cannot reach the server. Check your connection.' };
    }
  }

  return { message: error instanceof Error ? error.message : 'Something went wrong.' };
}

/**
 * Turn a stored image reference into something an <img> can load.
 *
 * Uploads are stored as an API-relative path (`/files/2026/08/<uuid>.jpg`)
 * rather than an absolute URL, so the same row keeps working when the API
 * moves host — which an absolute URL baked in at upload time would not. An
 * external https:// address is passed through untouched, both for rows that
 * predate uploads and for a future move to S3 or a CDN.
 */
export function resolveImageUrl(reference: string | null | undefined): string {
  if (!reference) return '';

  const value = reference.trim();
  if (/^https?:\/\//i.test(value)) return value;

  return `${api.defaults.baseURL ?? ''}${value.startsWith('/') ? value : `/${value}`}`;
}
